import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  SparklesIcon, 
  LightBulbIcon, 
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

const AIExplanationCard = ({ tabName, analysisData, className = "" }) => {
  const [explanation, setExplanation] = useState('');
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);

  const fetchExplanation = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.post('/api/portfolio/explain-tab', {
        tab_name: tabName,
        analysis_data: analysisData
      });
      
      setExplanation(response.data.explanation);
      setExpanded(true);
    } catch (err) {
      setError('Failed to get AI explanation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = () => {
    if (!explanation && !loading) {
      fetchExplanation();
    } else {
      setExpanded(!expanded);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`aura-card-emphasis ${className}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-pine-tint rounded-lg">
            <SparklesIcon className="h-5 w-5 text-pine" />
          </div>
          <div>
            <h3 className="text-lg font-display font-semibold text-primary flex items-center gap-2">
              <LightBulbIcon className="h-4 w-4 text-brass" />
              AI Portfolio Guide
            </h3>
            <p className="text-sm text-secondary">
              Learn what this means for your investments
            </p>
          </div>
        </div>
        
        <button
          onClick={toggleExpanded}
          disabled={loading}
          className="btn-secondary text-sm py-2 px-4 disabled:opacity-50"
        >
          {loading ? (
            <ArrowPathIcon className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <span className="font-medium">
                {expanded ? 'Hide' : 'Explain'}
              </span>
              {expanded ? (
                <ChevronUpIcon className="h-4 w-4 ml-1 inline" />
              ) : (
                <ChevronDownIcon className="h-4 w-4 ml-1 inline" />
              )}
            </>
          )}
        </button>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="border-t border-hairline pt-4">
              {error ? (
                <div className="border border-danger/40 bg-pine-tint rounded-lg p-4">
                  <p className="text-danger text-sm">{error}</p>
                  <button
                    onClick={fetchExplanation}
                    className="mt-2 text-pine hover:text-primary text-sm underline"
                  >
                    Try again
                  </button>
                </div>
              ) : (
                <div className="prose-editorial text-sm">
                  <div className="text-secondary leading-relaxed whitespace-pre-wrap">
                    {explanation || (
                      <div className="flex items-center gap-2 text-faint">
                        <ArrowPathIcon className="h-4 w-4 animate-spin" />
                        Generating personalized explanation...
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default AIExplanationCard;
